import { USE_MOCK_API } from "./config";
import { http, authStorage } from "./http";
import { ApiError, type AuthSession, type Role, type User } from "./types";
import * as mock from "../mocks/auth";

/**
 * Auth service. Every screen and component must call these functions and never
 * touch the network or mocks directly. When the real backend is ready, only the
 * bodies here change (swap USE_MOCK_API branches), screens stay untouched.
 */

export interface LoginInput {
  email: string;
  password: string;
}

type BackendRole = "SUPER_ADMIN" | "ADMIN" | "COLLECTION_AGENT" | "ACCOUNTANT" | "VIEWER";

interface BackendUser {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  role: BackendRole;
  isActive: boolean;
  lastLoginAt?: string | null;
}

interface BackendLoginResult {
  accessToken: string;
  refreshToken: string;
  user: BackendUser;
}

const ROLE_MAP: Record<BackendRole, Role> = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  COLLECTION_AGENT: "collection_agent",
  ACCOUNTANT: "accountant",
  VIEWER: "viewer",
};

function mapUser(backendUser: BackendUser): User {
  return {
    id: backendUser.id,
    name: backendUser.fullName,
    email: backendUser.email,
    phone: backendUser.phone ?? "",
    role: ROLE_MAP[backendUser.role],
  };
}

export async function login(input: LoginInput): Promise<AuthSession> {
  if (USE_MOCK_API) {
    try {
      const session = await mock.mockLogin(input.email, input.password);
      authStorage.set(session.token);
      return session;
    } catch (err) {
      throw new ApiError((err as Error).message, 401, "INVALID_CREDENTIALS");
    }
  }
  const result = await http<BackendLoginResult>("/auth/login", {
    method: "POST",
    body: input,
    withAuth: false,
  });
  authStorage.set(result.accessToken);
  authStorage.setRefresh(result.refreshToken);
  return { user: mapUser(result.user), token: result.accessToken };
}

export async function logout(): Promise<void> {
  if (USE_MOCK_API) {
    await mock.mockLogout();
    authStorage.clear();
    return;
  }
  try {
    const refreshToken = authStorage.getRefresh();
    if (refreshToken) {
      await http<void>("/auth/logout", { method: "POST", body: { refreshToken } });
    }
  } finally {
    authStorage.clear();
  }
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  if (USE_MOCK_API) {
    try {
      await mock.mockForgotPassword(email);
      return { message: "OTP sent to your email" };
    } catch (err) {
      throw new ApiError((err as Error).message, 400, "INVALID_INPUT");
    }
  }
  return await http<{ message: string }>("/auth/forgot-password", {
    method: "POST",
    body: { email },
    withAuth: false,
  });
}

export async function verifyOtp(email: string, otp: string): Promise<{ valid: boolean; message: string }> {
  if (USE_MOCK_API) {
    return { valid: true, message: "OTP verified successfully" };
  }
  return await http<{ valid: boolean; message: string }>("/auth/verify-otp", {
    method: "POST",
    body: { email, otp },
    withAuth: false,
  });
}

export async function resetPassword(email: string, otp: string, newPassword: string): Promise<{ message: string }> {
  if (USE_MOCK_API) {
    return { message: "Password reset successful" };
  }
  return await http<{ message: string }>("/auth/reset-password", {
    method: "POST",
    body: { email, otp, newPassword },
    withAuth: false,
  });
}

/** Restore the current user from a stored token, if any. */
export async function getCurrentUser(): Promise<User | null> {
  const token = authStorage.get();
  if (!token) return null;
  if (USE_MOCK_API) {
    return mock.mockGetCurrentUser(token);
  }
  try {
    const backendUser = await http<BackendUser>("/auth/me", { method: "GET" });
    return mapUser(backendUser);
  } catch {
    authStorage.clear();
    return null;
  }
}

export interface UpdateProfileInput {
  name: string;
  email: string;
  phone?: string;
}

export async function updateProfile(input: UpdateProfileInput): Promise<User> {
  if (USE_MOCK_API) {
    const current = await getCurrentUser();
    if (!current) throw new ApiError("User not logged in", 401, "UNAUTHORIZED");
    return { ...current, name: input.name, email: input.email, phone: input.phone ?? "" };
  }
  const result = await http<BackendUser>("/auth/profile", {
    method: "PUT",
    body: input,
  });
  return mapUser(result);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  if (USE_MOCK_API) {
    return;
  }
  await http<void>("/auth/change-password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}
